import { useEffect } from 'react';

/**
 * Polls /asset-manifest.json periodically. When the main.js the manifest points
 * to differs from the bundle THIS page is actually running, the page reloads
 * (only while the tab is visible, so we don't blow away in-progress work).
 *
 * Removes the need for users to hard-refresh after a deploy.
 *
 * ⚠️ El ancla es el <script> del DOM, NO el primer fetch del manifest. La
 * versión anterior guardaba `initial` del primer fetch: si CloudFront servía un
 * index.html viejo (bundle viejo) pero el manifest fresco, `initial` quedaba
 * "nuevo" y el hook nunca detectaba que la página misma era vieja (incidente
 * 2026-07-07: usuarios viendo el ranking anterior post-deploy).
 */

/** Path del bundle que está corriendo, leído del <script src> que CRA inyecta. */
const runningBundle = (): string | null => {
  const el = document.querySelector<HTMLScriptElement>('script[src*="/static/js/main."]');
  if (!el?.src) return null;
  try {
    return new URL(el.src, window.location.origin).pathname;
  } catch {
    return null;
  }
};

// Anti-loop: si el reload trae OTRA VEZ un HTML viejo (edge stale), sin guardia
// entraríamos en reload infinito. Máximo un auto-reload cada 5 min por tab.
const RELOAD_GUARD_KEY = 'drupi_vc_last_reload';
const RELOAD_GUARD_MS = 5 * 60_000;

export const useVersionCheck = (intervalMs = 60_000) => {
  useEffect(() => {
    let cancelled = false;
    // Fallback para el caso rarísimo de no encontrar el <script> (tests, SSR):
    // usamos el primer manifest como referencia, igual que la versión anterior.
    let fallbackInitial: string | null = null;

    const fetchManifest = async (): Promise<string | null> => {
      try {
        const res = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        return data?.files?.['main.js'] ?? null;
      } catch {
        return null;
      }
    };

    const reloadOnce = () => {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
      if (Date.now() - last < RELOAD_GUARD_MS) return;
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      window.location.reload();
    };

    const check = async () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      const manifest = await fetchManifest();
      if (!manifest) return;

      const running = runningBundle();
      if (running) {
        // Comparación directa: manifest dice qué DEBERÍA correr; el DOM dice
        // qué corre. Distinto → esta página es vieja → reload.
        if (manifest !== running) reloadOnce();
        return;
      }

      if (fallbackInitial === null) {
        fallbackInitial = manifest;
        return;
      }
      if (manifest !== fallbackInitial) reloadOnce();
    };

    check();
    const id = setInterval(check, intervalMs);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);
};
